/**
 * Ecclesia theme — a chmeetings-inspired light theme on plain MUI v5.
 *
 * Palette: teal accent (#15A37F) + cool greys + soft pastels.
 * Typography: Inter (falls back to system).
 * Radii: 12–16px on surfaces, 10px on buttons/inputs.
 * Shadows: very soft, 1–2 stops max.
 *
 * Loaded once in App.js via <ThemeProvider theme={chmeetings}>.
 */

import { createTheme } from "@mui/material/styles";

// Re-use MD2's compatibility helpers/tokens so legacy MD* wrapper components
// keep working under the new chmeetings theme.
import mdBoxShadows from "assets/theme/base/boxShadows";
import mdBorders from "assets/theme/base/borders";
import mdColors from "assets/theme/base/colors";
import mdTypography from "assets/theme/base/typography";
import boxShadow from "assets/theme/functions/boxShadow";
import hexToRgb from "assets/theme/functions/hexToRgb";
import linearGradient from "assets/theme/functions/linearGradient";
import pxToRem from "assets/theme/functions/pxToRem";
import rgba from "assets/theme/functions/rgba";

// Palette ---------------------------------------------------------------------

const teal = {
  50: "#E8F7F1",
  100: "#C7EDDC",
  200: "#9DDEC1",
  300: "#6DCFA5",
  400: "#3FBE8E",
  500: "#15A37F", // primary
  600: "#0F8F6E",
  700: "#0B785B",
  800: "#085F49",
  900: "#054636",
};

const ink = {
  50: "#FBFCFD",
  100: "#F5F7FA",
  200: "#EDF1F5",
  300: "#DCE3EB",
  400: "#B8C2CC",
  500: "#8C97A4",
  600: "#5F6B78",
  700: "#3F4A55",
  800: "#252D36",
  900: "#0F1419",
};

// Soft shadow tokens (much lighter than MD2's default).
const shadow1 = "0 1px 2px rgba(15,20,25,.04), 0 1px 3px rgba(15,20,25,.06)";
const shadow2 = "0 2px 6px rgba(15,20,25,.05), 0 4px 12px rgba(15,20,25,.06)";
const shadow3 = "0 6px 16px rgba(15,20,25,.08), 0 12px 24px rgba(15,20,25,.06)";

// MUI shadows is a 25-entry array (0 → 24).
const shadows = ["none"];
for (let i = 1; i <= 24; i++) {
  if (i <= 4) shadows.push(shadow1);
  else if (i <= 12) shadows.push(shadow2);
  else shadows.push(shadow3);
}

const chmeetings = createTheme({
  palette: {
    mode: "light",
    primary: { main: teal[500], light: teal[300], dark: teal[700], contrastText: "#fff" },
    secondary: { main: ink[700], light: ink[500], dark: ink[900], contrastText: "#fff" },
    success: { main: "#16A34A", light: "#34D27D", dark: "#0F7A37", contrastText: "#fff" },
    warning: { main: "#F59E0B", light: "#FBBF24", dark: "#B45309", contrastText: "#fff" },
    error: { main: "#DC2626", light: "#EF4444", dark: "#991B1B", contrastText: "#fff" },
    info: { main: "#0EA5E9", light: "#38BDF8", dark: "#0369A1", contrastText: "#fff" },
    background: {
      default: ink[100], // page background — subtle cool grey
      paper: "#FFFFFF",
    },
    text: {
      primary: ink[900],
      secondary: ink[600],
      disabled: ink[400],
      // MD2 reads palette.text.main / .focus
      main: ink[700],
      focus: ink[700],
    },
    divider: ink[200],
    // Custom tokens used by the shell. Glassmorphism sidebar — translucent
    // white over a backdrop blur (applied in Sidebar.js), so it floats above
    // the page rather than reading as a flat panel.
    sidebar: {
      bg: "rgba(255,255,255,0.55)",
      hover: "rgba(15,20,25,0.045)",
      activeBg: "rgba(21,163,127,0.14)",
      activeFg: teal[700],
      iconIdle: ink[500],
      border: "rgba(255,255,255,0.6)",
      textPrimary: ink[900],
      textSecondary: ink[600],
    },
    ink,
    teal,

    // ---- MD2 compatibility keys (so MDBox/MDTypography/etc still work) ----
    grey: mdColors.grey,
    white: mdColors.white,
    black: mdColors.black,
    transparent: mdColors.transparent,
    light: mdColors.light,
    dark: mdColors.dark,
    gradients: mdColors.gradients,
    socialMediaColors: mdColors.socialMediaColors,
    badgeColors: mdColors.badgeColors,
    inputColors: mdColors.inputColors,
    sliderColors: mdColors.sliderColors,
    circleSliderColors: mdColors.circleSliderColors,
    tabs: mdColors.tabs,
    coloredShadows: mdColors.coloredShadows,
  },

  // ---- MD2 compatibility tokens spread at theme root ----
  boxShadows: { ...mdBoxShadows },
  borders: { ...mdBorders },
  functions: { boxShadow, hexToRgb, linearGradient, pxToRem, rgba },

  shape: { borderRadius: 12 },

  shadows,

  // Spread MD2 typography first so legacy wrappers find size.xxs/lg/xl,
  // fontWeightLight/Medium/Bold, lineHeight.sm, etc.; then override the
  // bits we care about (font family + chmeetings type scale).
  typography: {
    ...mdTypography,
    fontFamily: [
      "Inter",
      "-apple-system",
      "BlinkMacSystemFont",
      "Segoe UI",
      "Roboto",
      "Helvetica Neue",
      "Arial",
      "sans-serif",
    ].join(","),
    h1: { ...mdTypography.h1, fontWeight: 700, fontSize: "2.25rem", letterSpacing: "-0.02em" },
    h2: { ...mdTypography.h2, fontWeight: 700, fontSize: "1.875rem", letterSpacing: "-0.02em" },
    h3: { ...mdTypography.h3, fontWeight: 700, fontSize: "1.5rem", letterSpacing: "-0.01em" },
    h4: { ...mdTypography.h4, fontWeight: 700, fontSize: "1.25rem", letterSpacing: "-0.01em" },
    h5: { ...mdTypography.h5, fontWeight: 700, fontSize: "1.125rem" },
    h6: { ...mdTypography.h6, fontWeight: 700, fontSize: "1rem" },
    subtitle1: { ...mdTypography.subtitle1, fontWeight: 600, fontSize: "0.9375rem" },
    subtitle2: {
      ...mdTypography.subtitle2,
      fontWeight: 600,
      fontSize: "0.8125rem",
      color: ink[600],
    },
    body1: { ...mdTypography.body1, fontSize: "0.9375rem", lineHeight: 1.55 },
    body2: { ...mdTypography.body2, fontSize: "0.875rem", lineHeight: 1.55 },
    button: { ...mdTypography.button, fontWeight: 600, textTransform: "none", letterSpacing: 0 },
    caption: { ...mdTypography.caption, fontSize: "0.75rem", color: ink[600] },
    overline: {
      ...mdTypography.overline,
      fontSize: "0.6875rem",
      letterSpacing: "0.08em",
      fontWeight: 600,
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: ink[100],
          color: ink[900],
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
        "::-webkit-scrollbar": { width: 10, height: 10 },
        "::-webkit-scrollbar-thumb": { backgroundColor: ink[300], borderRadius: 8 },
        "::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${ink[200]}`,
          boxShadow: shadow1,
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingInline: 18,
          paddingBlock: 9,
          fontWeight: 600,
        },
        containedPrimary: {
          boxShadow: "0 1px 2px rgba(21,163,127,.25)",
          "&:hover": { boxShadow: "0 4px 10px rgba(21,163,127,.30)" },
        },
        outlined: { borderColor: ink[300] },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#fff",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: ink[300] },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ink[400] },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: teal[500],
            borderWidth: 1.5,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: "0.875rem", color: ink[600] },
      },
    },

    MuiTextField: {
      defaultProps: { variant: "outlined", size: "medium" },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 600 },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: { borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 500 },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-root": {
            backgroundColor: ink[100],
            color: ink[600],
            fontSize: "0.75rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            borderBottom: `1px solid ${ink[200]}`,
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${ink[200]}`,
          fontSize: "0.875rem",
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, boxShadow: shadow3 },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: "transparent" },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: { borderRight: `1px solid ${ink[200]}` },
      },
    },
  },
});

export default chmeetings;
